import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Phone, Mail, Globe2, FileCheck, Tag, Boxes, Truck } from "lucide-react";
import { FadeUp, SectionHead } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import { COMPANY, IMAGES, whatsappLink } from "@/data/site";

const PROPS = [
  { icon: Truck, title: "Reliable Supply", text: "60+ SKUs with consistent production capacity for retail, wholesale and institutional buyers." },
  { icon: Globe2, title: "Export Ready", text: "Tasty Mint export-grade packs built for shelf life and international transit." },
  { icon: Tag, title: "Private Labelling", text: "Your brand, our craft — custom pack sizes and labelling for overseas partners." },
  { icon: FileCheck, title: "Documentation Support", text: "Complete export documentation and compliance assistance from our Barwani office." },
  { icon: Boxes, title: "Flexible Formats", text: "Retail pouches, family packs, bulk cartons and container-load programs." },
];

export default function ExportInquiry() {
  return (
    <>
      <section className="relative pt-44 pb-24 overflow-hidden" data-testid="export-hero">
        <img src={IMAGES.exportContainers} alt="Global shipping containers" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/85 to-ink" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] sm:text-xs uppercase tracking-overline text-gold mb-6">Export &amp; Distributor Inquiry</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[1.02] text-cream max-w-4xl">
              Partner with a<br /><em className="text-gold italic">60-year legacy.</em>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-300 font-light leading-relaxed">
              Distributor territories across India. Export partnerships worldwide. One enquiry is all it takes.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-ink" data-testid="export-body">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHead testId="export-why-head" overline="Why Waghe & Sons" title="Built for serious trade partners." />
            <div className="mt-12 space-y-7">
              {PROPS.map((p, i) => (
                <FadeUp key={p.title} delay={i * 0.07} className="flex gap-5 group">
                  <div className="w-12 h-12 shrink-0 border border-gold/30 rounded-sm flex items-center justify-center group-hover:bg-gold/10 transition-colors duration-300">
                    <p.icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-cream">{p.title}</h3>
                    <p className="mt-1.5 text-sm text-zinc-400 font-light leading-relaxed">{p.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.3} className="mt-12 flex flex-wrap gap-3">
              <a href={`tel:${COMPANY.phoneRaw[0]}`} data-testid="export-call-button" className="inline-flex items-center gap-2 border border-white/20 text-cream text-xs uppercase tracking-[0.14em] px-5 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors duration-300">
                <Phone className="w-3.5 h-3.5" /> {COMPANY.phones[0]}
              </a>
              <a href={`mailto:${COMPANY.email}?subject=Export / Distributor Enquiry`} data-testid="export-email-button" className="inline-flex items-center gap-2 border border-white/20 text-cream text-xs uppercase tracking-[0.14em] px-5 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors duration-300">
                <Mail className="w-3.5 h-3.5" /> {COMPANY.email}
              </a>
              <a href={whatsappLink("Namaste! I'm interested in a distributorship / export partnership.")} target="_blank" rel="noopener noreferrer" data-testid="export-whatsapp-button" className="inline-flex items-center gap-2 border border-[#1ebe5d]/60 text-[#4be389] text-xs uppercase tracking-[0.14em] px-5 py-3 rounded-sm hover:bg-[#1ebe5d] hover:text-white transition-colors duration-300">
                WhatsApp Trade Desk
              </a>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <div className="bg-coal border border-white/10 rounded-sm p-7 sm:p-10">
              <Tabs defaultValue="distributor" data-testid="inquiry-tabs">
                <TabsList className="bg-black/50 border border-white/10 rounded-sm p-1 h-auto w-full grid grid-cols-2">
                  <TabsTrigger
                    value="distributor"
                    data-testid="tab-distributor"
                    className="rounded-sm py-3 text-xs sm:text-sm uppercase tracking-[0.14em] data-[state=active]:bg-gold data-[state=active]:text-ink data-[state=active]:font-semibold text-zinc-400 transition-colors duration-300"
                  >
                    Dealer / Distributor
                  </TabsTrigger>
                  <TabsTrigger
                    value="export"
                    data-testid="tab-export"
                    className="rounded-sm py-3 text-xs sm:text-sm uppercase tracking-[0.14em] data-[state=active]:bg-gold data-[state=active]:text-ink data-[state=active]:font-semibold text-zinc-400 transition-colors duration-300"
                  >
                    Export Partner
                  </TabsTrigger>
                </TabsList>
                <div className="mt-8">
                  <TabsContent value="distributor">
                    <p className="text-sm text-zinc-400 font-light mb-7">
                      For Indian market distribution of the <span className="text-gold">Waghe Shree</span> range — tell us your territory and network.
                    </p>
                    <InquiryForm type="distributor" />
                  </TabsContent>
                  <TabsContent value="export">
                    <p className="text-sm text-zinc-400 font-light mb-7">
                      For international import, private label or container programs under <span className="text-emerald-light">Tasty Mint</span>.
                    </p>
                    <InquiryForm type="export" />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
